import React, { useState, useEffect } from 'react';
import { MdPerson, MdGroup, MdAdd, MdClose, MdCheckBox, MdCheckBoxOutlineBlank, MdSearch } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import { hooks } from '../hooks';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import { chatCache } from '../services/chatCacheService';
import { text } from '../text';

const PersonIcon = MdPerson as any;
const GroupIcon = MdGroup as any;
const AddIcon = MdAdd as any;
const CloseIcon = MdClose as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;
const SearchIcon = MdSearch as any;

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
};

export const ChatListView: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((s: any) => s.authSlice);
  const { t } = useLanguage();
  const tc = (t as any).chat;
  const imageBaseUrl = (process.env.REACT_APP_IMAGE_BASE_URL || '').replace(/\/$/, '');

  const [conversations, setConversations] = useState<any[]>([]);
  const [listSearch, setListSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const [mode, setMode] = useState<'pick' | 'group'>('pick');
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const cached = await chatCache.getConversationList(authSlice.userId);
      if (!cancelled && cached.length) {
        setConversations(cached.map((c) => ({
          id: c.conversation_id, type: c.type, title: c.title, photo: c.photo,
          participant_count: c.participant_count, last_message: c.last_message,
          updated_at: c.updated_at, is_unread: c.is_unread,
        })));
        setIsLoading(false);
      }
      try {
        const res = await axiosInstance.get('/chat/conversations');
        if (cancelled) return;
        setConversations(res.data || []);
        await chatCache.saveConversationList(authSlice.userId, res.data || []);
      } catch (err) {
        if (!cancelled) console.error('Error fetching conversations:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    if (authSlice.userId) load();
    return () => { cancelled = true; };
  }, [authSlice.userId]);

  const openNewSheet = async () => {
    setShowNewSheet(true);
    setMode('pick');
    setGroupName('');
    setSelectedIds([]);
    setUserSearch('');
    setErrorMsg('');
    try {
      const res = await axiosInstance.get('/users');
      const all = Array.isArray(res.data) ? res.data : (res.data.users || []);
      setUsers(all.filter((u: any) => u.id !== authSlice.userId));
    } catch {}
  };

  const filteredUsers = users.filter((u) => u.name?.toLowerCase().includes(userSearch.toLowerCase()));

  const openConversation = (conv: any) => {
    navigate('/ChatRoom', { state: { conversationId: conv.id, title: conv.title, type: conv.type, photo: conv.photo } });
  };

  const handlePickUser = async (user: any) => {
    try {
      const res = await axiosInstance.post(`/chat/conversations/direct/${user.id}`);
      setShowNewSheet(false);
      navigate('/ChatRoom', { state: { conversationId: res.data.conversation_id, title: user.name, type: 'direct', photo: user.profile_picture || null } });
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || tc?.errorLoad || 'Gagal membuka chat');
    }
  };

  const toggleMember = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) { setErrorMsg(tc?.errorGroupName || 'Nama grup wajib diisi'); return; }
    if (selectedIds.length === 0) { setErrorMsg(tc?.errorSelectMembers || 'Pilih minimal 1 anggota'); return; }
    setCreating(true);
    setErrorMsg('');
    try {
      const res = await axiosInstance.post('/chat/conversations/group', { name: groupName.trim(), participant_ids: selectedIds });
      setShowNewSheet(false);
      navigate('/ChatRoom', { state: { conversationId: res.data.conversation_id, title: res.data.name, type: 'group', photo: null } });
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || tc?.errorLoad || 'Gagal membuat grup');
    } finally {
      setCreating(false);
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!listSearch.trim()) return true;
    const q = listSearch.toLowerCase();
    return conv.title?.toLowerCase().includes(q) || conv.last_message?.body?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div
        onClick={openNewSheet}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
          border: '1.5px dashed #93C5FD', borderRadius: 12, backgroundColor: '#EFF6FF',
          padding: '12px 16px', marginBottom: 12,
        }}
      >
        <AddIcon size={18} color="#3B82F6" />
        <text.T14 style={{ color: '#3B82F6' }}>{tc?.newChat || 'Chat Baru'}</text.T14>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#F3F4F6', borderRadius: 10, padding: '9px 12px', marginBottom: 12 }}>
        <SearchIcon size={16} color="#9CA3AF" />
        <input
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
          placeholder={tc?.searchChatsPlaceholder || 'Cari nama atau pesan...'}
          style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1, backgroundColor: 'transparent', color: theme.colors.mainDark }}
        />
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>{t.common.loading}</div>
      ) : conversations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>{tc?.noConversations || 'Belum ada percakapan'}</div>
      ) : filteredConversations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 14 }}>{tc?.noResults || 'Tidak ditemukan'}</div>
      ) : (
        filteredConversations.map((conv) => (
          <div
            key={conv.id}
            onClick={() => openConversation(conv)}
            style={{
              display: 'flex', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12,
              padding: '12px 14px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              border: '1px solid #F4F6F8', gap: 12, cursor: 'pointer',
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: '#E5E7EB', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {conv.photo ? (
                <img src={`${imageBaseUrl}/${conv.photo}`} alt={conv.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : conv.type === 'group' ? (
                <GroupIcon size={22} color="#9CA3AF" />
              ) : (
                <PersonIcon size={22} color="#9CA3AF" />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: conv.is_unread ? 700 : 600, color: theme.colors.mainDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {conv.title}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: conv.is_unread ? theme.colors.mainDark : '#6B7280', fontWeight: conv.is_unread ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {conv.last_message ? (conv.last_message.body || (conv.last_message.attachment_name ? `📎 ${conv.last_message.attachment_name}` : '')) : ''}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
              <text.T10 style={{ color: '#9CA3AF' }}>{timeAgo(conv.updated_at)}</text.T10>
              {conv.is_unread && <div style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: theme.colors.mainColor }} />}
            </div>
          </div>
        ))
      )}

      {showNewSheet && (
        <div
          onClick={() => setShowNewSheet(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 650, backgroundColor: '#fff', borderRadius: '16px 16px 0 0', padding: 16, maxHeight: '80vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <text.T16 style={{ fontWeight: '700', color: theme.colors.mainDark }}>
                {mode === 'pick' ? (tc?.newMessage || 'Pesan Baru') : (tc?.newGroup || 'Grup Baru')}
              </text.T16>
              <div onClick={() => setShowNewSheet(false)} style={{ cursor: 'pointer' }}><CloseIcon size={22} color="#6B7280" /></div>
            </div>

            {errorMsg && (
              <div style={{ backgroundColor: '#FEE2E2', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                <text.T12 style={{ color: '#DC2626' }}>{errorMsg}</text.T12>
              </div>
            )}

            {mode === 'pick' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#F3F4F6', borderRadius: 10, padding: '9px 12px', marginBottom: 10 }}>
                  <SearchIcon size={16} color="#9CA3AF" />
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder={tc?.searchPlaceholder || 'Cari pengguna...'}
                    style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1, backgroundColor: 'transparent', color: theme.colors.mainDark }}
                  />
                </div>
                <div
                  onClick={() => setMode('group')}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', marginBottom: 8 }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GroupIcon size={18} color={theme.colors.mainColor} />
                  </div>
                  <text.T14 style={{ color: theme.colors.mainColor, fontWeight: '600' }}>{tc?.newGroup || 'Grup Baru'}</text.T14>
                </div>
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => handlePickUser(u)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#E5E7EB', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {u.profile_picture ? (
                        <img src={`${imageBaseUrl}/${u.profile_picture}`} alt={u.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <PersonIcon size={16} color="#9CA3AF" />
                      )}
                    </div>
                    <text.T14 style={{ color: theme.colors.mainDark }}>{u.name}</text.T14>
                  </div>
                ))}
              </>
            ) : (
              <>
                <input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder={tc?.groupNamePlaceholder || 'Nama grup'}
                  style={{
                    width: '100%', border: '1px solid #D1D5DB', borderRadius: 10, padding: '10px 14px',
                    fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
                    color: theme.colors.mainDark, marginBottom: 12,
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#F3F4F6', borderRadius: 10, padding: '9px 12px', marginBottom: 10 }}>
                  <SearchIcon size={16} color="#9CA3AF" />
                  <input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder={tc?.searchPlaceholder || 'Cari pengguna...'}
                    style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1, backgroundColor: 'transparent', color: theme.colors.mainDark }}
                  />
                </div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 8, display: 'block' }}>
                  {tc?.selectMembers || 'Pilih Anggota'} {selectedIds.length > 0 ? `(${selectedIds.length} ${tc?.membersSelected || 'anggota dipilih'})` : ''}
                </text.T12>
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                  {filteredUsers.map((u, idx) => {
                    const isSelected = selectedIds.includes(u.id);
                    return (
                      <div
                        key={u.id}
                        onClick={() => toggleMember(u.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
                          borderBottom: idx < filteredUsers.length - 1 ? '1px solid #F3F4F6' : 'none',
                          backgroundColor: isSelected ? '#EFF6FF' : '#fff',
                        }}
                      >
                        {isSelected ? <CheckBoxIcon size={20} color={theme.colors.mainColor} /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
                        <text.T14 style={{ color: theme.colors.mainDark, flex: 1 }}>{u.name}</text.T14>
                      </div>
                    );
                  })}
                </div>
                <div
                  onClick={creating ? undefined : handleCreateGroup}
                  style={{
                    textAlign: 'center', padding: 12, borderRadius: 10, backgroundColor: theme.colors.mainColor,
                    cursor: creating ? 'default' : 'pointer', opacity: creating ? 0.6 : 1,
                  }}
                >
                  <text.T14 style={{ color: '#fff', fontWeight: '600' }}>{tc?.createGroup || 'Buat Grup'}</text.T14>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
